import React, { useState, useEffect, useCallback } from 'react';
import { useSelectedBrand } from '../hooks/useApi';
import LoadingSpinner from '../components/LoadingSpinner';
import apiService from '../services/api';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const Calendar = () => {
  const { selectedBrandId, setSelectedBrandId, brands: brandOptions, brandsLoading } = useSelectedBrand();
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCalendar = useCallback(async () => {
    if (!selectedBrandId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getCalendarData(selectedBrandId, year, month);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBrandId, year, month]);

  useEffect(() => { fetchCalendar(); }, [fetchCalendar]);

  const navigateMonth = (delta) => {
    let newMonth = month + delta;
    let newYear = year;
    if (newMonth < 1) { newMonth = 12; newYear--; }
    if (newMonth > 12) { newMonth = 1; newYear++; }
    setMonth(newMonth);
    setYear(newYear);
  };

  if (brandsLoading) return <LoadingSpinner />;

  const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'long' });

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Content Calendar</h1>
          <p className="text-sm text-gray-500 mt-1">Visual cadence tracking with gap detection</p>
        </div>
        {brandOptions.length > 1 && (
          <select
            value={selectedBrandId || ''}
            onChange={e => setSelectedBrandId(Number(e.target.value))}
            className="border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500"
          >
            {brandOptions.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm">{error}</div>
      )}

      {loading ? <LoadingSpinner /> : data && (
        <>
          {/* Month nav */}
          <div className="flex items-center justify-between bg-white rounded-2xl border border-gray-200 p-4">
            <button onClick={() => navigateMonth(-1)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
              <i className="fas fa-chevron-left text-gray-600"></i>
            </button>
            <h2 className="text-lg font-semibold text-gray-900">{monthName} {year}</h2>
            <button onClick={() => navigateMonth(1)} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
              <i className="fas fa-chevron-right text-gray-600"></i>
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{data.total_published}</div>
              <div className="text-xs text-gray-500">Published</div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-red-500">{data.total_missed}</div>
              <div className="text-xs text-gray-500">Missed</div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-gray-700">{data.total_target}</div>
              <div className="text-xs text-gray-500">Target</div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 p-4 text-center">
              <div className="text-2xl font-bold text-accent-600">{data.weeks_on_target_streak}</div>
              <div className="text-xs text-gray-500">Week Streak</div>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {DAYS.map(d => (
                <div key={d} className="text-center text-xs font-semibold text-gray-400 uppercase py-2">{d}</div>
              ))}
            </div>

            {/* Calendar cells */}
            <div className="grid grid-cols-7 gap-1">
              {/* Leading empty cells */}
              {data.days.length > 0 && Array.from({ length: data.days[0].day_of_week }).map((_, i) => (
                <div key={`empty-${i}`} className="h-20" />
              ))}

              {data.days.map(day => {
                const dayNum = parseInt(day.date.split('-')[2]);
                const isToday = day.date === new Date().toISOString().split('T')[0];

                let bgColor = 'bg-white';
                let borderColor = 'border-gray-100';
                if (day.is_gap) {
                  bgColor = 'bg-red-50';
                  borderColor = 'border-red-200';
                } else if (day.published_count > 0 && day.is_target_day) {
                  bgColor = 'bg-green-50';
                  borderColor = 'border-green-200';
                } else if (day.is_future && day.is_target_day) {
                  bgColor = 'bg-blue-50';
                  borderColor = 'border-blue-200';
                }

                return (
                  <div
                    key={day.date}
                    className={`h-20 rounded-xl border p-2 ${bgColor} ${borderColor} ${isToday ? 'ring-2 ring-accent-400' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-sm ${isToday ? 'font-bold text-accent-700' : 'text-gray-700'}`}>
                        {dayNum}
                      </span>
                      {day.is_target_day && (
                        <span className="text-xs text-gray-400">
                          {day.target_count}x
                        </span>
                      )}
                    </div>

                    <div className="mt-1 flex gap-1 flex-wrap">
                      {day.posts.map((p, j) => (
                        <span
                          key={j}
                          className={`w-3 h-3 rounded-full ${
                            p.status === 'PUBLISHED' ? 'bg-green-400' :
                            p.status === 'SCHEDULED' ? 'bg-blue-400' :
                            p.status === 'DRAFT' ? 'bg-amber-400' :
                            'bg-gray-300'
                          }`}
                          title={p.status}
                        />
                      ))}
                    </div>

                    {day.is_gap && (
                      <div className="text-xs text-red-400 mt-0.5">GAP</div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex gap-4 mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-green-400 inline-block"></span> Published</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-400 inline-block"></span> Scheduled</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-400 inline-block"></span> Draft</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-50 border border-red-200 rounded inline-block"></span> Gap</span>
            </div>
          </div>

          {/* Target days info */}
          {data.target_days.length > 0 && (
            <div className="bg-accent-50 rounded-2xl border border-accent-200 p-4 text-sm text-accent-700">
              <i className="fas fa-info-circle mr-2"></i>
              Target posting days: {data.target_days.map(d => DAYS[d]).join(', ')}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Calendar;
